import React, { useState, useEffect } from 'react';
import {
  Button,
  Drawer,
  DrawerHeader,
  DrawerFooter,
  DrawerBody,
  TextField,
} from '@one-thd/sui-atomic-components';
import { formatPhoneNumber } from '@src/utils/general';

interface PhoneNumberDrawerProps {
  open: boolean;
  onClose: () => void;
  onSave: (phoneNumber: string) => void;
  initialPhoneNumber?: string;
}

export const PhoneNumberDrawer: React.FC<PhoneNumberDrawerProps> = ({
  open,
  onClose,
  onSave,
  initialPhoneNumber = '',
}) => {
  const [formPhoneNumber, setFormPhoneNumber] = useState(initialPhoneNumber);
  const [isPhoneValid, setIsPhoneValid] = useState(true);

  const handlePhoneInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    event?.preventDefault();
    const { value } = event.target;
    // Formatting phone number
    const digits = value.replace(/\D/g, '');
    if (digits.length > 10) return; // Ignore anything over 10 digits
    setIsPhoneValid(true);
    setFormPhoneNumber(formatPhoneNumber(value));
  };

  const onPhoneFormSubmit = (event?: React.FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    const sanitizedPhoneNumber = formPhoneNumber?.replace(/\D/g, '');
    if (sanitizedPhoneNumber?.length !== 10) {
      setIsPhoneValid(false);
      return;
    }
    onSave(sanitizedPhoneNumber);
    onClose();
  };

  // Reset form when drawer opens/closes
  useEffect(() => {
    if (open) {
      setFormPhoneNumber(initialPhoneNumber);
      setIsPhoneValid(true);
    }
  }, [open, initialPhoneNumber]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      position="right"
      className="drawer-z-index-fix"
    >
      <DrawerHeader
        title="Phone Number"
        onClose={onClose}
        data-testid="Phone-Number"
      />
      <DrawerBody>
        <form autoComplete="off" noValidate onSubmit={onPhoneFormSubmit}>
          <TextField
            required
            fullWidth
            id="phone-number"
            placeholder="___-___-____"
            name="phoneNumber"
            label="Phone Number"
            type="tel"
            value={formPhoneNumber}
            onChange={handlePhoneInputChange}
            statusMessage={!isPhoneValid && 'Invalid Phone Number'}
            status={!isPhoneValid && 'error'}
          />
          {/* This hidden button allows the onSubmit callback to get called when user presses the enter key within a field since actual submission button is not within this form object */}
          <button type="submit" aria-hidden="true" />
        </form>
      </DrawerBody>
      <DrawerFooter className="sui-bg-primary sui-p-4 sui-relative sui-bottom-0 sui-sticky sui-shadow-sm">
        <div className="sui-flex sui-gap-4 sui-flex-col">
          <Button fullWidth onClick={onPhoneFormSubmit}>
            Update
          </Button>
          <Button fullWidth variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </DrawerFooter>
    </Drawer>
  );
};
